import React, { useEffect, useState } from "react";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import ViewsChart from "../../components/ViewsChart";
import VisitorWorldMap from "../../components/VisitorWorldMap";
import "./PortfolioAnalytics.css";

function PortfolioAnalytics() {
  const [views, setViews] = useState(0);
  const [downloads, setDownloads] = useState(0);
  const [countries, setCountries] = useState({});
  const [devices, setDevices] = useState({ desktop: 0, mobile: 0, tablet: 0 });
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFirebaseAnalytics = async () => {
      setLoading(true);
      // 1. Portfolio Views
      try {
        const viewRef = doc(db, "analytics", "portfolioViews");
        const viewSnap = await getDoc(viewRef);
        if (viewSnap.exists()) setViews(viewSnap.data().count || 0);
      } catch (err) {
        console.error("Views fetch error:", err);
      }

      // 2. Resume Downloads
      try {
        const downloadRef = doc(db, "analytics", "resumeDownloads");
        const downloadSnap = await getDoc(downloadRef);
        if (downloadSnap.exists()) setDownloads(downloadSnap.data().count || 0);
      } catch (err) {
        console.error("Downloads fetch error:", err);
      }

      // 3. Visitor Countries
      try {
        const visitorRef = doc(db, "visitors", "visitorData");
        const visitorSnap = await getDoc(visitorRef);
        if (visitorSnap.exists()) setCountries(visitorSnap.data().countries || {});
      } catch (err) {
        console.error("Countries fetch error:", err);
      }

      // 4. Device Breakdown
      try {
        const deviceRef = doc(db, "analytics", "devices");
        const deviceSnap = await getDoc(deviceRef);
        if (deviceSnap.exists()) setDevices(deviceSnap.data() || {});
      } catch (err) {
        console.error("Devices fetch error:", err);
      }

      // 5. Visitor Timeline
      try {
        const timelineRef = collection(db, "visitors", "visitorData", "timeline");
        const timelineSnap = await getDocs(timelineRef);
        let visits = [];
        timelineSnap.forEach((docSnap) => visits.push(docSnap.data()));
        visits.sort((a, b) => {
          const timeA = a.timestamp || (a.time ? new Date(a.time).getTime() : 0) || 0;
          const timeB = b.timestamp || (b.time ? new Date(b.time).getTime() : 0) || 0;
          return timeB - timeA;
        });
        setTimeline(visits.slice(0, 10));
      } catch (err) {
        console.error("Error fetching timeline logs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFirebaseAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="loading-spinner-box" style={{ padding: "80px", textAlign: "center" }}>
        <div className="spinner"></div>
        <p style={{ color: "#38bdf8", marginTop: "12px" }}>Loading Firebase Analytics Data...</p>
      </div>
    );
  }

  return (
    <div className="portfolio-analytics-container">
      <div className="page-header-row">
        <div>
          <h2>📈 Portfolio Firebase Analytics</h2>
          <p className="page-sub">Real-time visitor telemetry, location, and interaction metrics</p>
        </div>
      </div>

      {/* METRICS ROW */}
      <div className="analytics-metrics-grid">
        <div className="dash-card glass-glow">
          <span className="card-icon">👀</span>
          <h3>Portfolio Views</h3>
          <p className="card-value">{views.toLocaleString()}</p>
        </div>

        <div className="dash-card glass-glow">
          <span className="card-icon">📄</span>
          <h3>Resume Downloads</h3>
          <p className="card-value">{downloads.toLocaleString()}</p>
        </div>
      </div>

      {/* CHARTS GRID */}
      <div className="dash-two-col">
        <div className="dash-glass-panel">
          <h2>📊 Weekly Impression Trends</h2>
          <ViewsChart />
        </div>

        <div className="dash-glass-panel">
          <VisitorWorldMap />
        </div>
      </div>

      {/* DEVICES & COUNTRIES & TIMELINE GRID */}
      <div className="analytics-three-grid">
        {/* DEVICES */}
        <div className="dash-glass-panel">
          <h2>💻 Devices Breakdown</h2>
          <div className="analytics-stat-list">
            <p>🖥️ Desktop — <b>{devices.desktop || 0}</b></p>
            <p>📱 Mobile — <b>{devices.mobile || 0}</b></p>
            <p>📟 Tablet — <b>{devices.tablet || 0}</b></p>
          </div>
        </div>

        {/* COUNTRIES */}
        <div className="dash-glass-panel">
          <h2>🌍 Visitor Countries</h2>
          <div className="analytics-stat-list">
            {Object.entries(countries).length > 0 ? (
              Object.entries(countries).map(([country, count]) => (
                <p key={country}>
                  🚩 {country} — <b>{count}</b>
                </p>
              ))
            ) : (
              <p>No country data logged yet.</p>
            )}
          </div>
        </div>

        {/* TIMELINE */}
        <div className="dash-glass-panel">
          <h2>🕒 Recent Visitor Log</h2>
          <div className="analytics-stat-list timeline-list">
            {timeline.length > 0 ? (
              timeline.map((visit, index) => (
                <p key={index} className="timeline-item-p">
                  <span>{visit.time}</span>
                  <b>{visit.country || "Unknown"} ({visit.device || "Browser"})</b>
                </p>
              ))
            ) : (
              <p>No recent activity logs.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortfolioAnalytics;
